import { CircularProgress } from '@mui/material';
import { filter, find, map, orderBy, some } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UseQueryResult, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  assetCurrencyApi,
  authApi,
  banksApi,
  currencyExchangeApi,
  fiatCurrencyApi,
  merchantsApi,
  metaApi,
  paymentTypesApi,
  tradeMethodsApi,
  usersApi,
  sciDomainsApi,
  p2pProvidersApi,
  assetCurrencyExchangeApi,
  cascadesApi,
} from 'api';
import {
  LayoutMenuSection,
  MainLayout,
  SecureConfirmProvider,
} from 'components';
import { META_KEY } from 'constants/meta.contants';
import { DEFAULT_ASSET_CURRENCY_CODE } from 'constants/order.constants';
import { ROUTE_PATH } from 'constants/routes';
import { UserContext, UserContextProps } from 'context';
import {
  AppConfigArea,
  AppConfigKey,
  OrderStatusReason,
  QueryKey,
  UserRole,
} from 'enums';
import {
  useAppConfigValue,
  useBanksQuery,
  useMutation,
  useTradeMethodsQuery,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  Merchant,
  Meta,
  P2PProviderSummary,
  SidebarHeaderConfig,
  User,
} from 'types';
import { authUtils, returnUrlUtils } from 'utils';

type Props = {
  menuSections?: LayoutMenuSection[];
  children: React.ReactElement;
};

export const UserLayout: React.FC<Props> = ({ menuSections, children }) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'layout.user',
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const localLogout = useCallback(() => {
    const email = authUtils.getUserEmail();
    const returnUrl = returnUrlUtils.getCurrentUrl();
    authUtils.resetAuthData();
    navigate(ROUTE_PATH.PUBLIC.LOGIN, { state: { email, returnUrl } });
  }, [navigate]);

  const profileQueryResult = useQuery(QueryKey.Profile, usersApi.getProfile, {
    onError: () => {
      // authUtils.logout has already handled the logout and navigated away
      if (!authUtils.getAuthData()) {
        return;
      }
      // if first load
      if (!profileQueryResult.data) {
        toast.error(t('load_profile_error'));
        localLogout();
      }
    },
  });
  const user = useMemo(
    () => profileQueryResult.data as User,
    [profileQueryResult],
  );

  const isAdminOrTechOperator = useMemo(
    () => [UserRole.Admin, UserRole.TechOperator].includes(user?.role),
    [user],
  );
  const isMerchant = useMemo(() => user?.role === UserRole.Merchant, [user]);

  const { mutate: logout } = useMutation(authApi.logout, {
    onSettled: () => {
      queryClient.clear();
      localLogout();
    },
  });

  const fiatCurrenciesQueryResult = useQuery(
    QueryKey.FiatCurrencies,
    fiatCurrencyApi.getAll,
  );

  const assetCurrenciesQueryResult = useQuery(
    QueryKey.AssetCurrencies,
    assetCurrencyApi.getAll,
  );

  const banksQueryResult = useBanksQuery(QueryKey.Banks, banksApi.getAll);
  const banksRawQueryResultNotFormatted = useQuery(
    QueryKey.Banks,
    banksApi.getAll,
  );

  const paymentTypesQueryResult = useQuery(
    QueryKey.PaymentTypes,
    paymentTypesApi.getAll,
  );

  const allTradeMethodsQueryResult = useTradeMethodsQuery(
    QueryKey.TradeMethods,
    tradeMethodsApi.getAll,
  );

  const tradeMethods = useMemo(
    () =>
      filter(
        allTradeMethodsQueryResult.data,
        (tradeMethod) => !tradeMethod.archived,
      ),
    [allTradeMethodsQueryResult.data],
  );

  const currencyExchangeQueryResult = useQuery(
    QueryKey.CurrencyExchange,
    currencyExchangeApi.getAll,
  );

  const assetCurrencyExchangeQueryResult = useQuery(
    QueryKey.AssetCurrencyExchange,
    assetCurrencyExchangeApi.getAll,
    {
      enabled: isAdminOrTechOperator,
    },
  );

  const merchantQueryResult = useQuery(
    QueryKey.MerchantMyInfo,
    merchantsApi.getMyInfo,
    { enabled: isMerchant },
  );

  const metaQueryResult = useQuery(QueryKey.Meta, metaApi.getAll);

  const sidebarHeaderQueryResult = useAppConfigValue({
    area: AppConfigArea.Appearance,
    key: AppConfigKey.SidebarHeader,
  });

  const payinOrderStatusReasonsQueryResult = useAppConfigValue({
    area: AppConfigArea.Platform,
    key: AppConfigKey.OrderStatusReasons,
    isPublic: true,
  });

  const disputeCompletedStatusReasonsQueryResult = useAppConfigValue({
    area: AppConfigArea.Platform,
    key: AppConfigKey.DisputeCompletedStatusReasons,
    isPublic: true,
  });

  // we put meta to context in child components only, so we access by metaQueryResult?.data here
  const ordersMonitoringEnabled = useMemo(() => {
    const value = find(
      metaQueryResult?.data,
      (item: Meta) => item.key === META_KEY.ORDERS_MONITORING_ENABLED,
    )?.value;
    return value === 'true';
  }, [metaQueryResult.data]);

  const merchantOperatorsEnabled = useMemo(() => {
    const value = find(
      metaQueryResult?.data,
      (item: Meta) => item.key === META_KEY.MERCHANT_OPERATORS_ENABLED,
    )?.value;
    return value === 'true';
  }, [metaQueryResult.data]);

  // TODO: implement isHidden: (meta) => boolean for section items
  const visibleMenuSections = useMemo(
    () =>
      map(menuSections, (section) => ({
        ...section,
        items: filter(section.items, (item) => {
          if (item.link === ROUTE_PATH.TRADER.ORDER_MONITORING) {
            return ordersMonitoringEnabled;
          }
          if (
            item.link === ROUTE_PATH.MERCHANT.OPERATORS ||
            item.link === ROUTE_PATH.MERCHANT.GROUPS
          ) {
            return merchantOperatorsEnabled;
          }
          return true;
        }),
      })).filter((section) => section.items.length > 0),
    [menuSections, ordersMonitoringEnabled, merchantOperatorsEnabled],
  );

  const sciDomainsQueryResult = useQuery(
    QueryKey.SciDomains,
    sciDomainsApi.getAll,
  );

  const p2pProvidersQueryResult = useQuery(
    [QueryKey.P2PProviders, 'summary'],
    () => p2pProvidersApi.getSummary<P2PProviderSummary>(),
  );

  const cascadesQueryResult = useQuery(
    QueryKey.Cascades,
    cascadesApi.getAllAsRole(user?.role),
    { enabled: isAdminOrTechOperator },
  );

  const contextValue: UserContextProps = useMemo(
    () => ({
      user,
      merchant: merchantQueryResult.data as Merchant | undefined,
      assetCurrencies: orderBy(
        assetCurrenciesQueryResult.data || [],
        ({ code }) => code === DEFAULT_ASSET_CURRENCY_CODE,
        'desc',
      ),
      sidebarHeaderConfig:
        sidebarHeaderQueryResult.data as any as SidebarHeaderConfig,
      fiatCurrencies: fiatCurrenciesQueryResult.data || [],
      banksQueryResult,
      banks: banksQueryResult.data || [],
      banksRaw: banksRawQueryResultNotFormatted.data || [],
      paymentTypes: paymentTypesQueryResult.data || [],
      orderStatusReasons:
        (payinOrderStatusReasonsQueryResult.data as any as OrderStatusReason[]) ||
        [],
      disputeCompletedStatusReasons:
        (disputeCompletedStatusReasonsQueryResult.data as any as OrderStatusReason[]) ||
        [],
      tradeMethods,
      allTradeMethods: allTradeMethodsQueryResult.data || [],
      currencyExchanges: currencyExchangeQueryResult.data || [],
      meta: metaQueryResult.data || [],
      sciDomains: sciDomainsQueryResult.data || [],
      p2pProviders: p2pProvidersQueryResult.data || [],
      assetCurrencyExchange: assetCurrencyExchangeQueryResult.data || [],
      cascades: (cascadesQueryResult.data as any) || [],
    }),
    [
      user,
      merchantQueryResult.data,
      assetCurrenciesQueryResult.data,
      sidebarHeaderQueryResult.data,
      fiatCurrenciesQueryResult.data,
      banksQueryResult,
      banksRawQueryResultNotFormatted.data,
      paymentTypesQueryResult.data,
      payinOrderStatusReasonsQueryResult.data,
      disputeCompletedStatusReasonsQueryResult.data,
      tradeMethods,
      allTradeMethodsQueryResult.data,
      currencyExchangeQueryResult.data,
      metaQueryResult.data,
      sciDomainsQueryResult.data,
      p2pProvidersQueryResult.data,
      assetCurrencyExchangeQueryResult.data,
      cascadesQueryResult.data,
    ],
  );

  const queries = useMemo(
    () => [
      assetCurrenciesQueryResult,
      fiatCurrenciesQueryResult,
      banksQueryResult,
      banksRawQueryResultNotFormatted,
      paymentTypesQueryResult,
      sidebarHeaderQueryResult,
      allTradeMethodsQueryResult,
      ...(isAdminOrTechOperator
        ? [assetCurrencyExchangeQueryResult, cascadesQueryResult]
        : []),
      ...(isMerchant ? [merchantQueryResult] : []),
      metaQueryResult,
    ],
    [
      assetCurrenciesQueryResult,
      banksQueryResult,
      banksRawQueryResultNotFormatted,
      sidebarHeaderQueryResult,
      fiatCurrenciesQueryResult,
      paymentTypesQueryResult,
      allTradeMethodsQueryResult,
      assetCurrencyExchangeQueryResult,
      cascadesQueryResult,
      merchantQueryResult,
      metaQueryResult,
      isMerchant,
      isAdminOrTechOperator,
    ],
  );

  if (
    !user ||
    some<UseQueryResult>(queries, (query) => !query.data || query.isLoading)
  ) {
    return (
      <div className="tw-text-center tw-my-8">
        <CircularProgress />
      </div>
    );
  }

  return (
    <UserContext.Provider value={contextValue}>
      <SecureConfirmProvider>
        <MainLayout menuSections={visibleMenuSections} onLogout={logout}>
          {children}
        </MainLayout>
      </SecureConfirmProvider>
    </UserContext.Provider>
  );
};
